import axios from "axios";
import { isOwnerPanelUser } from "@/lib/ownerAccess";

export const STATIC_FIREBASE =
  process.env.REACT_APP_STATIC_FIREBASE === "true";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = STATIC_FIREBASE ? "" : `${BACKEND_URL}/api`;

const axiosApi = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
});

axiosApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("ugp_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const FIREBASE_DATABASE_URL = (
  process.env.REACT_APP_FIREBASE_DATABASE_URL ||
  "https://taco-chat-c1539-default-rtdb.firebaseio.com"
).replace(/\/+$/, "");
const FIREBASE_STATE_PATH = (
  process.env.REACT_APP_FIREBASE_STATE_PATH ||
  "rooms/_deluxeAppState/state"
).replace(/^\/+|\/+$/g, "");
const UGP_FIREBASE_NAMESPACE =
  process.env.REACT_APP_UGP_FIREBASE_NAMESPACE || "ultimateGameStash";
const OWNER_NAMES = new Set(
  (process.env.REACT_APP_OWNER_NAMES || "CarterB,London,RyanH")
    .split(",")
    .map((name) => normalizeAccountKey(name))
    .filter(Boolean)
);

const PAGE_FILES = {
  chat: "chat_html.html",
  community: "community_html.html",
  gamemaker: "gamemaker_html.html",
  party: "party_html.html",
};

let gameIndexPromise = null;
let gameIndex = null;

const staticApi = {
  get: async (path, config = {}) => makeResponse(await staticRoute("GET", path, null, config)),
  post: async (path, body, config = {}) => makeResponse(await staticRoute("POST", path, body, config)),
  put: async (path, body, config = {}) => makeResponse(await staticRoute("PUT", path, body, config)),
  delete: async (path, config = {}) => makeResponse(await staticRoute("DELETE", path, null, config)),
};

export const api = STATIC_FIREBASE ? staticApi : axiosApi;

function makeResponse(data) {
  return { data };
}

function makeApiError(status, detail) {
  const error = new Error(detail);
  error.response = { status, data: { detail } };
  return error;
}

function normalizeAccountKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[.#$/[\]\s]/g, "");
}

function encodeFirebaseSegment(value) {
  return encodeURIComponent(String(value));
}

function firebaseUrl(segments = []) {
  const encoded = segments.map(encodeFirebaseSegment).join("/");
  const suffix = encoded ? `/${encoded}` : "";
  return `${FIREBASE_DATABASE_URL}/${FIREBASE_STATE_PATH}${suffix}.json`;
}

async function firebaseRequest(segments, options = {}) {
  const response = await fetch(firebaseUrl(segments), {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    throw makeApiError(response.status, `Firebase request failed (${response.status})`);
  }
  if (options.method === "DELETE") return null;
  return response.json();
}

const firebaseGet = (segments) => firebaseRequest(segments);
const firebasePut = (segments, value) =>
  firebaseRequest(segments, { method: "PUT", body: JSON.stringify(value) });
const firebasePatch = (segments, value) =>
  firebaseRequest(segments, { method: "PATCH", body: JSON.stringify(value) });
const firebaseDelete = (segments) =>
  firebaseRequest(segments, { method: "DELETE" });

async function fetchJson(path) {
  const response = await fetch(path, { cache: "no-store" });
  if (!response.ok) {
    throw makeApiError(response.status, `Could not load ${path}`);
  }
  return response.json();
}

async function fetchText(path) {
  const response = await fetch(path, { cache: "no-store" });
  if (!response.ok) {
    throw makeApiError(response.status, `Could not load ${path}`);
  }
  return response.text();
}

function publicPath(path) {
  if (!path) return "";
  return path.startsWith("/") ? path : `/${path}`;
}

async function getStaticGameIndex() {
  if (!gameIndexPromise) {
    gameIndexPromise = fetchJson("/games/index.json").then((items) => {
      gameIndex = Array.isArray(items) ? items : [];
      return gameIndex;
    });
  }
  return gameIndexPromise;
}

function keywordsForName(name) {
  return String(name || "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length > 1);
}

function toGameSummary(item) {
  return {
    slug: item.slug,
    name: item.name,
    keywords: item.keywords || keywordsForName(item.name),
    file: item.file,
  };
}

async function listGamesStatic(q = "") {
  const items = (await getStaticGameIndex()).map(toGameSummary);
  const query = String(q || "").trim().toLowerCase();
  if (!query) return items;
  return items.filter(
    (game) =>
      game.name.toLowerCase().includes(query) ||
      game.slug.toLowerCase().includes(query) ||
      game.keywords.some((keyword) => keyword.includes(query))
  );
}

async function getGameStatic(slug) {
  const items = await getStaticGameIndex();
  const item = items.find((game) => game.slug === slug);
  if (!item) throw makeApiError(404, "Game not found");
  return {
    ...toGameSummary(item),
    html: "",
  };
}

function staticGameEmbedUrl(slug) {
  const item = gameIndex?.find((game) => game.slug === slug);
  if (item?.file) return publicPath(item.file);
  return `/games/${encodeURIComponent(slug)}.html`;
}

function isOwnerAccount(id, account = {}) {
  const key = normalizeAccountKey(id || account.username);
  const role = String(account.role || "").toLowerCase();
  const badges = Array.isArray(account.badges) ? account.badges : [];
  return (
    OWNER_NAMES.has(key) ||
    role === "owner" ||
    badges.some((badge) => String(badge).toLowerCase() === "owner")
  );
}

function hasSiteApprovalStatus(account = {}) {
  return Boolean(String(account.ugpStatus || account.ugp_status || "").trim());
}

function shouldCreateAccessRequest(id, account = {}) {
  return !isOwnerAccount(id, account) && !hasSiteApprovalStatus(account) && !account.banned;
}

async function ensureAccessRequest(id, account) {
  if (!shouldCreateAccessRequest(id, account)) return account;
  const now = Date.now();
  const patch = {
    ugpStatus: "pending",
    ugpRequestedAt: now,
    updatedAt: now,
  };
  await firebasePatch(["accounts", id], patch);
  return { ...account, ...patch };
}

function isoFromAccountDate(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return new Date(value).toISOString();
  }
  if (typeof value === "string" && value) return value;
  return new Date().toISOString();
}

function accountToUser(id, account) {
  if (!account || account.ugpDeleted) return null;
  const owner = isOwnerAccount(id, account);
  const banned = !!account.banned;
  const status = owner
    ? "approved"
    : account.ugpStatus || account.ugp_status || (banned ? "rejected" : "pending");
  return {
    id,
    username: account.username || id,
    role: owner ? "owner" : "user",
    status,
    created_at: isoFromAccountDate(account.createdAt || account.created_at),
  };
}

async function getAccount(id) {
  return firebaseGet(["accounts", id]);
}

async function getCurrentStaticUser() {
  const token = localStorage.getItem("ugp_token") || "";
  if (!token.startsWith("static-firebase:")) {
    throw makeApiError(401, "Please sign in again");
  }
  const id = decodeURIComponent(token.replace("static-firebase:", ""));
  const account = await getAccount(id);
  const user = accountToUser(id, account);
  if (!user) throw makeApiError(401, "Please sign in again");
  return user;
}

async function requireOwnerPanelStatic() {
  const user = await getCurrentStaticUser();
  if (!isOwnerPanelUser(user)) {
    throw makeApiError(403, "Owner panel is restricted to CarterB");
  }
  return user;
}

async function authLoginStatic(username, password) {
  const id = normalizeAccountKey(username);
  if (!id || !password) throw makeApiError(401, "Login failed");
  const account = await getAccount(id);
  if (!account || account.ugpDeleted || String(account.password || "") !== String(password)) {
    throw makeApiError(401, "Login failed");
  }
  const accountWithAccessRequest = await ensureAccessRequest(id, account);
  const user = accountToUser(id, accountWithAccessRequest);
  if (!user) throw makeApiError(401, "Login failed");
  return {
    token: `static-firebase:${encodeURIComponent(id)}`,
    user,
  };
}

async function authRegisterStatic(username, password) {
  const cleanUsername = String(username || "").trim();
  const id = normalizeAccountKey(cleanUsername);
  if (!id || cleanUsername.length < 2) {
    throw makeApiError(400, "Username is required");
  }
  if (String(password || "").length < 6) {
    throw makeApiError(400, "Password must be at least 6 characters");
  }
  const existing = await getAccount(id);
  if (existing && !existing.ugpDeleted) {
    throw makeApiError(409, "That username already exists");
  }
  const now = Date.now();
  const account = {
    username: cleanUsername,
    password,
    role: "member",
    status: "online",
    theme: "ember",
    mood: "Social",
    avatar: "",
    badges: ["New"],
    banned: false,
    chatPlus: false,
    xp: 0,
    ugpStatus: "pending",
    createdAt: now,
    updatedAt: now,
    lastSeen: now,
    lastActive: now,
  };
  await firebasePut(["accounts", id], account);
  return {
    token: `static-firebase:${encodeURIComponent(id)}`,
    user: accountToUser(id, account),
  };
}

async function ownerListUsersStatic() {
  await requireOwnerPanelStatic();
  const accounts = (await firebaseGet(["accounts"])) || {};
  return Object.entries(accounts)
    .map(([id, account]) => accountToUser(id, account))
    .filter(Boolean)
    .sort((a, b) => a.username.localeCompare(b.username));
}

async function ownerSetUserStatusStatic(id, action) {
  await requireOwnerPanelStatic();
  const status = action === "approve" ? "approved" : "rejected";
  const updated = await firebasePatch(["accounts", id], {
    ugpStatus: status,
    updatedAt: Date.now(),
  });
  return accountToUser(id, updated);
}

async function ownerSetUserRoleStatic(id, role) {
  await requireOwnerPanelStatic();
  const nextRole = role === "owner" ? "owner" : "member";
  const patch = {
    role: nextRole,
    updatedAt: Date.now(),
  };
  if (nextRole === "owner") patch.ugpStatus = "approved";
  const updated = await firebasePatch(["accounts", id], patch);
  return accountToUser(id, updated);
}

async function ownerDeleteUserStatic(id) {
  await requireOwnerPanelStatic();
  await firebasePatch(["accounts", id], {
    ugpDeleted: true,
    ugpStatus: "rejected",
    updatedAt: Date.now(),
  });
  return { ok: true };
}

async function getPageHtmlStatic(pageKey) {
  const key = PAGE_FILES[pageKey] ? pageKey : "chat";
  const saved = await firebaseGet([UGP_FIREBASE_NAMESPACE, "pages", key]).catch(() => null);
  if (typeof saved === "string" && saved.trim()) return saved;
  if (saved?.html) return saved.html;
  return fetchText(`/pages/${PAGE_FILES[key]}`);
}

async function setPageHtmlStatic(pageKey, html) {
  await requireOwnerPanelStatic();
  const key = PAGE_FILES[pageKey] ? pageKey : "chat";
  await firebasePut([UGP_FIREBASE_NAMESPACE, "pages", key], String(html || ""));
  return { key, html: String(html || "") };
}

function suggestionFromEntry(id, value) {
  if (!value) return null;
  return {
    id,
    user_id: value.user_id || value.userId || "",
    username: value.username || "",
    game_name: value.game_name || value.gameName || "",
    notes: value.notes || "",
    status: value.status || "open",
    created_at: value.created_at || value.createdAt || new Date().toISOString(),
  };
}

async function listSuggestionsObject() {
  return (await firebaseGet([UGP_FIREBASE_NAMESPACE, "suggestions"]).catch(() => null)) || {};
}

async function createSuggestionStatic(game_name, notes) {
  const user = await getCurrentStaticUser();
  const id = `sugg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const suggestion = {
    id,
    user_id: user.id,
    username: user.username,
    game_name,
    notes,
    status: "open",
    created_at: new Date().toISOString(),
  };
  await firebasePut([UGP_FIREBASE_NAMESPACE, "suggestions", id], suggestion);
  return suggestion;
}

async function listMySuggestionsStatic() {
  const user = await getCurrentStaticUser();
  const suggestions = await listSuggestionsObject();
  return Object.entries(suggestions)
    .map(([id, value]) => suggestionFromEntry(id, value))
    .filter((item) => item && item.user_id === user.id)
    .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
}

async function ownerListSuggestionsStatic() {
  await requireOwnerPanelStatic();
  const suggestions = await listSuggestionsObject();
  return Object.entries(suggestions)
    .map(([id, value]) => suggestionFromEntry(id, value))
    .filter(Boolean)
    .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
}

async function ownerUpdateSuggestionStatic(id, action) {
  await requireOwnerPanelStatic();
  const status =
    action === "done" ? "done" : action === "dismiss" ? "dismissed" : "open";
  const updated = await firebasePatch([UGP_FIREBASE_NAMESPACE, "suggestions", id], {
    status,
    updated_at: new Date().toISOString(),
  });
  return suggestionFromEntry(id, updated);
}

async function ownerDeleteSuggestionStatic(id) {
  await requireOwnerPanelStatic();
  await firebaseDelete([UGP_FIREBASE_NAMESPACE, "suggestions", id]);
  return { ok: true };
}

async function ownerImportBackupStatic() {
  await requireOwnerPanelStatic();
  const [metadata, games, users, suggestions] = await Promise.all([
    fetchJson("/metadata.json").catch(() => null),
    getStaticGameIndex(),
    ownerListUsersStatic().catch(() => []),
    ownerListSuggestionsStatic().catch(() => []),
  ]);
  return {
    games: metadata?.counts?.games || games.length,
    pages: Object.keys(PAGE_FILES).length,
    users: users.length,
    suggestions: suggestions.length,
    favorites: metadata?.counts?.favorites || 0,
    notes: ["The deployed site is already using the packaged backup library."],
  };
}

async function staticRoute(method, rawPath, body, config = {}) {
  const path = String(rawPath || "").split("?")[0];

  if (method === "GET" && path === "/games") {
    return listGamesStatic(config.params?.q || "");
  }
  const gameMatch = path.match(/^\/games\/([^/]+)$/);
  if (method === "GET" && gameMatch) {
    return getGameStatic(decodeURIComponent(gameMatch[1]));
  }

  const pageMatch = path.match(/^\/pages\/([^/]+)$/);
  if (method === "GET" && pageMatch) {
    const key = decodeURIComponent(pageMatch[1]);
    return { key, html: await getPageHtmlStatic(key) };
  }
  const ownerPageMatch = path.match(/^\/owner\/pages\/([^/]+)$/);
  if (method === "PUT" && ownerPageMatch) {
    return setPageHtmlStatic(decodeURIComponent(ownerPageMatch[1]), body?.html || "");
  }
  if (method === "GET" && path === "/chat/html") {
    return { html: await getPageHtmlStatic("chat") };
  }
  if (method === "PUT" && path === "/owner/chat-html") {
    return setPageHtmlStatic("chat", body?.html || "");
  }

  const roleMatch = path.match(/^\/owner\/users\/([^/]+)\/role$/);
  if (method === "POST" && roleMatch) {
    return ownerSetUserRoleStatic(decodeURIComponent(roleMatch[1]), body?.role);
  }

  throw makeApiError(404, "Static route not found");
}

// --- Auth ---
export const authRegister = async (username, password) => {
  if (STATIC_FIREBASE) return authRegisterStatic(username, password);
  const { data } = await api.post("/auth/register", { username, password });
  return data;
};
export const authLogin = async (username, password) => {
  if (STATIC_FIREBASE) return authLoginStatic(username, password);
  const { data } = await api.post("/auth/login", { username, password });
  return data;
};
export const authMe = async () => {
  if (STATIC_FIREBASE) return getCurrentStaticUser();
  const { data } = await api.get("/auth/me");
  return data;
};

// --- Games ---
export const listGames = async (q = "") => {
  if (STATIC_FIREBASE) return listGamesStatic(q);
  const { data } = await api.get("/games", { params: q ? { q } : {} });
  return data;
};

// Session-cached full library - makes back-nav to home instant, expires in 5 min.
const GAMES_CACHE_KEY = "ugp:games:v1";
const GAMES_CACHE_TTL = 5 * 60 * 1000;
export const listGamesCached = async () => {
  try {
    const raw = sessionStorage.getItem(GAMES_CACHE_KEY);
    if (raw) {
      const { ts, data } = JSON.parse(raw);
      if (Date.now() - ts < GAMES_CACHE_TTL && Array.isArray(data)) return data;
    }
  } catch { /* ignore corrupt cache */ }
  const data = await listGames();
  try {
    sessionStorage.setItem(GAMES_CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch { /* quota / private mode */ }
  return data;
};
export const invalidateGamesCache = () => {
  try { sessionStorage.removeItem(GAMES_CACHE_KEY); } catch { /* ignore */ }
};
export const prefetchGameEmbed = (slug) => {
  try {
    const link = document.createElement("link");
    link.rel = "prefetch";
    link.as = "document";
    link.href = gameEmbedUrl(slug);
    document.head.appendChild(link);
    setTimeout(() => link.remove(), 15000);
  } catch { /* ignore */ }
};
export const getGame = async (slug) => {
  if (STATIC_FIREBASE) return getGameStatic(slug);
  return (await api.get(`/games/${slug}`)).data;
};
export const gameEmbedUrl = (slug) => {
  if (STATIC_FIREBASE) return staticGameEmbedUrl(slug);
  return `${API_BASE}/games/${slug}/embed`;
};
export const pageEmbedUrl = (pageKey) => {
  if (STATIC_FIREBASE) {
    return `/page-embed.html?page=${encodeURIComponent(pageKey)}&t=${encodeURIComponent(pageKey)}`;
  }
  return `${API_BASE}/pages/${pageKey}/embed?t=${pageKey}`;
};

// --- Chat ---
export const getChatHtml = async () => (await api.get("/chat/html")).data;
export const setChatHtml = async (html) =>
  (await api.put("/owner/chat-html", { html })).data;

// --- Owner ---
export const ownerListUsers = async () => {
  if (STATIC_FIREBASE) return ownerListUsersStatic();
  return (await api.get("/owner/users")).data;
};
export const ownerSetUserStatus = async (id, action) => {
  if (STATIC_FIREBASE) return ownerSetUserStatusStatic(id, action);
  return (await api.post(`/owner/users/${id}`, { action })).data;
};
export const ownerDeleteUser = async (id) => {
  if (STATIC_FIREBASE) return ownerDeleteUserStatic(id);
  return (await api.delete(`/owner/users/${id}`)).data;
};

// --- Suggestions ---
export const createSuggestion = async (game_name, notes) => {
  if (STATIC_FIREBASE) return createSuggestionStatic(game_name, notes);
  return (await api.post("/suggestions", { game_name, notes })).data;
};
export const listMySuggestions = async () => {
  if (STATIC_FIREBASE) return listMySuggestionsStatic();
  return (await api.get("/suggestions/mine")).data;
};
export const ownerListSuggestions = async () => {
  if (STATIC_FIREBASE) return ownerListSuggestionsStatic();
  return (await api.get("/owner/suggestions")).data;
};
export const ownerUpdateSuggestion = async (id, action) => {
  if (STATIC_FIREBASE) return ownerUpdateSuggestionStatic(id, action);
  return (await api.post(`/owner/suggestions/${id}`, { action })).data;
};
export const ownerDeleteSuggestion = async (id) => {
  if (STATIC_FIREBASE) return ownerDeleteSuggestionStatic(id);
  return (await api.delete(`/owner/suggestions/${id}`)).data;
};

// --- Owner: Games ---
export const ownerCreateGame = async (name, html) => {
  if (STATIC_FIREBASE) {
    throw makeApiError(400, "Packaged games require a new deploy to add or edit.");
  }
  const { data } = await api.post("/owner/games", { name, html });
  invalidateGamesCache();
  return data;
};
export const ownerUpdateGame = async (slug, name, html) => {
  if (STATIC_FIREBASE) {
    throw makeApiError(400, "Packaged games require a new deploy to add or edit.");
  }
  const { data } = await api.put(`/owner/games/${slug}`, { name, html });
  invalidateGamesCache();
  return data;
};
export const ownerDeleteGame = async (slug) => {
  if (STATIC_FIREBASE) {
    throw makeApiError(400, "Packaged games require a new deploy to remove.");
  }
  const { data } = await api.delete(`/owner/games/${slug}`);
  invalidateGamesCache();
  return data;
};
export const ownerImportBackup = async (file) => {
  if (STATIC_FIREBASE) return ownerImportBackupStatic(file);
  const body = new FormData();
  body.append("file", file);
  const { data } = await api.post("/owner/import-backup", body, {
    timeout: 10 * 60 * 1000,
  });
  invalidateGamesCache();
  return data;
};
